import React, { useRef, useState } from 'react';
import { View, TextInput, Pressable, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import CatalogSortDropdown from '@/components/Catalog/CatalogSortDropdown';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';

function MemoryFilterTabs({ searchQuery, onSearchChange, activeFilter, onFilterChange, counts,
  sortOrder, onSortChange, resultCount, selectionMode, onToggleSelectionMode }) {
  const { colors, theme } = useTheme();
  const palette = getCatalogPalette(theme);
  const { t } = useTranslation();
  const [showSearch, setShowSearch] = useState(Boolean(searchQuery));
  const inputRef = useRef(null);
  const filters = [
    { id: 'all', label: t('pages.memory.filterAll') },
    { id: 'learned', label: t('pages.memory.filterLearned') },
    { id: 'manual', label: t('pages.memory.filterManual') },
    { id: 'recent', label: t('pages.memory.filterRecent') },
  ].map(option => ({ ...option, label: `${option.label} (${counts[option.id]})` }));
  const sorts = [
    { id: 'newest', label: t('pages.common.newestFirst') },
    { id: 'oldest', label: t('pages.common.oldestFirst') },
    { id: 'alphabetical', label: t('pages.memory.sortAlphabetical') },
  ];
  const buttonStyle = ({ hovered, pressed }) => ({ minHeight: 36, paddingHorizontal: 9, borderRadius: 9,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
    backgroundColor: hovered || pressed ? palette.hover : 'transparent' });
  return (
    <View style={{ gap: 14, marginBottom: 16 }}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1, minWidth: 170, gap: 9 }}>
          <Text tag="h2" size="l" weight="semibold">{t('pages.memory.savedMemories')}</Text>
          <Text size="s" color="secondary">{counts.all}</Text>
        </View>
        <Pressable onPress={() => { onSearchChange(''); setShowSearch(value => !value); }}
          accessibilityRole="button" accessibilityLabel={showSearch ? t('pages.memory.closeSearch') : t('pages.memory.searchPlaceholder')}
          accessibilityState={{ expanded: showSearch }} {...(Platform.OS === 'web' ? { 'aria-expanded': showSearch } : {})} style={buttonStyle}>
          <Icon name={showSearch ? 'xmark' : 'magnifying-glass'} size={15} color={colors.text_color_secondary} />
        </Pressable>
        <CatalogSortDropdown value={activeFilter} options={filters} onChange={onFilterChange}
          label={t('pages.memory.filterLabel')} showLabel={false} />
        <CatalogSortDropdown value={sortOrder} options={sorts} onChange={onSortChange}
          label={t('sidebar.sort')} showLabel={false} />
        <Pressable onPress={onToggleSelectionMode} accessibilityRole="button"
          accessibilityLabel={selectionMode ? t('pages.memory.clearSelection') : t('common.select')} style={buttonStyle}>
          <Text size="xs" weight="medium" color="secondary">{selectionMode ? t('common.done') : t('common.select')}</Text>
        </Pressable>
      </View>
      {(showSearch || Boolean(searchQuery)) && <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10,
        minHeight: 44, paddingHorizontal: 14, borderRadius: 12, backgroundColor: palette.surface, borderWidth: 1, borderColor: palette.border }}>
        <Icon name="magnifying-glass" size={14} color={colors.text_color_secondary} />
        <TextInput ref={inputRef} autoFocus value={searchQuery} onChangeText={onSearchChange}
          accessibilityLabel={t('pages.memory.searchPlaceholder')} placeholder={t('pages.memory.searchPlaceholder')}
          placeholderTextColor={colors.text_color_secondary}
          style={{ flex: 1, minWidth: 0, fontSize: 15, paddingVertical: 12, color: colors.text_color_primary, ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}) }} />
      </View>}
      {(searchQuery || activeFilter !== 'all') && <Text size="xs" color="secondary" accessibilityLiveRegion="polite">
        {t('pages.memory.resultCount', { count: resultCount, total: counts.all })}
      </Text>}
    </View>
  );
}

export default React.memo(MemoryFilterTabs);
